import React from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'

import { useAgentSuggestedPrompts } from '~/hooks/useAgentSuggestedPrompts'
import { InputMethod } from '~/utils/trackingProvider'

import { ActionError, useAgentChat } from '../AgentChatContext'
import ChatInput from './ChatInput'
import ChatMessagesView from './ChatMessagesView'
import { SuggestedPrompts } from './SuggestedPrompts'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-inline-alert.css'
import styles from './AgentChat.module.scss'

const LOADING_OTHER_TAB_MESSAGE_ID = 'loading-other-tab'

const AgentChat = () => {
  const {
    messages,
    isLoading,
    isRespondingInAnotherTab,
    sendMessage,
    error,
    actionError,
    dismissActionError,
    startSession,
    isResponding,
    session,
  } = useAgentChat()
  const { t } = useTranslation('Agent')
  const suggestedPrompts = useAgentSuggestedPrompts()

  const hasAnyMessages = messages.some((msg) => !msg.isLoading)
  const isAnyTabResponding = isLoading || isRespondingInAnotherTab

  const displayMessages = React.useMemo(() => {
    // Only show loading if agent is responding AND the last message is from the user
    const lastNonLoadingMessage = [...messages]
      .reverse()
      .find((msg) => !msg.isLoading)

    if (
      isRespondingInAnotherTab &&
      !messages.some((msg) => msg.isLoading) &&
      lastNonLoadingMessage?.sender === 'user'
    ) {
      return [
        ...messages,
        {
          id: LOADING_OTHER_TAB_MESSAGE_ID,
          content: '',
          sender: 'agent' as const,
          timestamp: '',
          type: 'text' as const,
          isLoading: true,
        },
      ]
    }
    return messages
  }, [messages, isRespondingInAnotherTab])

  return (
    <div className={styles.chatContainer}>
      {actionError && (
        <div className={styles.actionErrorContainer}>
          <KibTopLevelAlert
            dismissible
            severity="error"
            onDismiss={dismissActionError}
          >
            {t(`Agent:${ActionError[actionError]}`)}
          </KibTopLevelAlert>
        </div>
      )}
      <ChatMessagesView
        isLoading={isLoading}
        messages={displayMessages}
        sessionId={session?.id ?? null}
      />
      {error && (
        <div className={styles.errorContainer}>
          <KibTopLevelAlert heading={t('Agent:ERROR')} severity="error">
            <div className={styles.errorContent}>
              {error}
              <div className={styles.retryButtonContainer}>
                <KibButtonNew
                  disabled={isAnyTabResponding}
                  size="small"
                  theme="utility"
                  onClick={startSession}
                >
                  {t('Agent:RETRY')}
                </KibButtonNew>
              </div>
            </div>
          </KibTopLevelAlert>
        </div>
      )}
      {!hasAnyMessages && !error && (
        <SuggestedPrompts
          suggestedPrompts={suggestedPrompts}
          onSuggestedPromptClick={(prompt) => {
            sendMessage({
              content: prompt.prompt,
              inputMethod: InputMethod.SUGGESTED_PROMPT,
              promptMetadata: {
                prompt_id: prompt.id,
                prompt_text: prompt.buttonText,
              },
            })
          }}
        />
      )}
      <ChatInput
        disabled={isAnyTabResponding || Boolean(error)}
        isResponding={isResponding}
        placeholder={t('Agent:ASK_AI_ASSISTANT')}
        sessionId={session?.id ?? null}
        onSendMessage={sendMessage}
      />
    </div>
  )
}

export default AgentChat
